import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { CATEGORY_SLUGS } from "@shared/constants/categories";
import { SECTION_SLUGS } from "@shared/constants/sections";
import { FIELD_KEYS } from "@shared/constants/fields";
import { parseContact, formatContactSummary } from "@shared/contact";
import { boardStepCh2, type Ch2Option, type Ch2Step } from "@shared/interview/board-ch2";
import type { GenericEntry } from "@shared/schema";
import { useSectionFields } from "../useSectionFields";
import { useSectionRows, type SectionRow } from "../useSectionRows";
import { cardValues } from "../YouScreen";
import { useHome, type Tenure } from "./useHome";
import { CH2_HOME_TEMPLATE } from "@shared/interview/board-template";
import { parseAddress } from "@shared/address";
import { useLocation } from "wouter";
import { saveInBackground, useSaveFailure } from "../saveFailure";
import { TilePicker, type TileSelection } from "../TilePicker";
import { NoteCard } from "../InterviewCells";
import { OnePanelProvider } from "../useOnePanelOpen";
import { useReportNoneAnswer } from "../useNoneAnswer";
import { Ch2Lines, Ch2Table, type Ch2RowIO } from "./Ch2Engine";
import { fieldOf, isRecordRow } from "./ch2Bindings";
import type { ReactNode } from "react";

/**
 * s232 (chapter two, PR B) — Your home, Other property, Vehicles and Valuables, built from board 2
 * (`Chapter2.dc.html`: the `home`, `prop`, `veh` and `val` steps; D-231.93, s232 rulings 1–6).
 *
 * 🔴 Every word, tile, column, option and note comes from the board at run time (rule 137): the tiles are the
 * step's `options`, the lines and tables are the engine's (./Ch2Engine), and where each answer is stored is the map's
 * (./ch2Bindings → board-map.ts). This file holds only what a board cannot say (STANDING-RULES § How screens get
 * built): WHICH record a tile's answers belong to.
 *
 * Storage — typed records from day one (D-229.68(a)), through each section's one row surface (`/typed-rows`):
 *  • Your home — the tile IS About You's "Do you own or rent your home?" (home_tenure, the s109 capture source);
 *    I own it → the plan's Primary Residence property; I rent / I live with family → the Things I Rent row marked
 *    as where the planner lives (is_residence). A new row starts at the planner's home address (rule 42).
 *  • Other property — owned places are the other Properties rows; rented places are the other Things I Rent rows.
 *  • Vehicles — the Vehicle List. Valuables — the Valuables rows that are not firearms (Firearms is its own screen).
 *
 * The plan's EXISTING records show on arrival (item 6): a typed row (its id is its record's) with anything in it.
 * A seeded `__seeded__…` scaffold is never a record and is not shown. Turning a tile off, a None tile and × HIDE
 * rows (G8) — nothing is deleted.
 */

const PROP = boardStepCh2("prop");
const VEH = boardStepCh2("veh");
const VAL = boardStepCh2("val");

const tilesOf = (step: Ch2Step) => (step.options ?? []).filter((o) => !o.none).map(({ id, label, sub, mark, color }) => ({ id, label, sub, mark, color }));
const noneOf = (step: Ch2Step) => {
  const o = (step.options ?? []).find((x) => x.none);
  return o ? { label: o.label, sub: o.sub, prune: o.prune } : undefined;
};
const optionOf = (step: Ch2Step, id: string): Ch2Option => (step.options ?? []).find((o) => o.id === id)!;
// A row is an answer when it holds a value OR a decline on anything asked (s230: a declined answer is a real answer).
const hasAny = (r: SectionRow, keys: readonly string[]) => keys.some((k) => (r.values[k] ?? "").trim() !== "" || (r.declined ?? []).includes(k));

/**
 * s232 staging walk: "Is there a mortgage (loan) on it?" is a new column, empty on every plan made before it — but a plan
 * whose property or vehicle already HAS its secured debt has answered it. The debt's own fields read Yes; a stored
 * answer always wins, and nothing is written until the planner answers. Pure.
 */
export function withSecuredAnswer(values: Readonly<Record<string, string>>): Record<string, string> {
  const pairs: [string, string[]][] = [
    [FIELD_KEYS.HAS_MORTGAGE, [FIELD_KEYS.MORTGAGE_BALANCE, FIELD_KEYS.MORTGAGE_LENDER, FIELD_KEYS.MORTGAGE_PAYMENT, FIELD_KEYS.MORTGAGE_ACCOUNT_NUMBER]],
    [FIELD_KEYS.HAS_LOAN, [FIELD_KEYS.LOAN_BALANCE, FIELD_KEYS.LOAN_LENDER, FIELD_KEYS.LOAN_PAYMENT]],
  ];
  const out = { ...values };
  for (const [flag, debt] of pairs) if (!(out[flag] ?? "") && debt.some((k) => (out[k] ?? "").trim() !== "")) out[flag] = "true";
  return out;
}

/** About You's card (a singleton, D-229.94): the home's answer, the home address, and who the spouse is. s237: exported —
 *  chapter one's "Is this home…" reads and writes the home through it too (one About You writer, rule 159). */
export function useAboutYou(clientId: string, onAnswered: () => void) {
  const { categoryId, categoryType, sectionId, fieldIdByKey } = useSectionFields(clientId, CATEGORY_SLUGS.ABOUT_YOU, SECTION_SLUGS.ABOUT_YOU);
  const keyByFieldId = useMemo(() => { const m = new Map<string, string>(); fieldIdByKey.forEach((id, key) => m.set(id, key)); return m; }, [fieldIdByKey]);
  const { data: entries } = useQuery<GenericEntry[]>({
    queryKey: ["/api/clients", clientId, "generic-entries", categoryType],
    queryFn: async () => (await apiRequest("GET", `/api/clients/${clientId}/generic-entries?categoryType=${categoryType}`)).json(),
    enabled: !!clientId && !!categoryType,
  });
  const values = useMemo(() => cardValues(entries ?? [], sectionId, keyByFieldId), [entries, sectionId, keyByFieldId]);
  const onSaveFailed = useSaveFailure();
  // Class C-server-deduped: About You is one record; a second fire writes the same value to the same cell.
  const mutation = useMutation({
    mutationFn: async ({ fieldId, value }: { fieldId: string; value: string }) =>
      await apiRequest("POST", `/api/clients/${clientId}/typed-rows`, { categoryId, sectionId, rowId: null, cells: [{ fieldId, value }] }),
    onError: onSaveFailed,
  });
  const write = async (key: string, value: string) => {
    const fieldId = fieldIdByKey.get(key);
    if (!fieldId || !sectionId || !categoryId) return;
    await mutation.mutateAsync({ fieldId, value });
    void queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "generic-entries"] });
    onAnswered();
  };
  return { ready: entries !== undefined && fieldIdByKey.size > 0, values, write };
}

/** The s225 build requirement: the gift question is not asked when the co-owner IS the planner's married spouse. Pure. */
export function coOwnerIsSpouse(about: Readonly<Record<string, string>>, coOwner: string): boolean {
  if ((about[FIELD_KEYS.MARITAL_STATUS] ?? "") !== "Married") return false;
  // A typed link cell's ref is a person's id and About You's spouse cell may hold a contact's, so the same person is
  // matched by ref OR by name — the layer's own reuse-by-name rule. No match asks, which is the board's default.
  const spouse = parseContact(about[FIELD_KEYS.SPOUSE_NAME] ?? "");
  const co = parseContact(coOwner ?? "");
  if (spouse?.ref && co?.ref && spouse.ref === co.ref) return true;
  const name = (v: string) => formatContactSummary(v).trim().toLowerCase().replace(/s+/g, " ");
  return name(about[FIELD_KEYS.SPOUSE_NAME] ?? "") !== "" && name(about[FIELD_KEYS.SPOUSE_NAME] ?? "") === name(coOwner ?? "");
}

/** Each row's IO for the engine: its values and the section's row surface. */
/**
 * s232 ruling-4 walk (client@): naming someone on the title IS the answer "not sole" — so on a place stored as Sole
 * ownership (a classic page's answer), the co-owner's save clears it first, in the same request. Without this, the
 * co-owner's gate (open unless Sole) blanked the name on save, and How do you own it together? never appeared. Pure.
 */
export function withSoleCleared(values: Readonly<Record<string, string>>, changes: Record<string, string>): Record<string, string> {
  const naming = (changes[FIELD_KEYS.JOINT_OWNER_NAME] ?? "").trim() !== "";
  if (!naming || values[FIELD_KEYS.TITLE_OWNERSHIP] !== "Sole ownership" || FIELD_KEYS.TITLE_OWNERSHIP in changes) return changes;
  return { [FIELD_KEYS.TITLE_OWNERSHIP]: "", ...changes }; // the title first: the gate opens before the name is written
}

function rowIO(clientId: string, data: ReturnType<typeof useSectionRows>, r: SectionRow, extra?: Partial<Ch2RowIO>): Ch2RowIO {
  const save = extra?.save ?? ((changes: Record<string, string>) => data.writeCells(r.rowId, changes));
  return {
    rowId: r.rowId, values: withSecuredAnswer(r.values), specify: r.specify, clientId, fieldIdByKey: data.fieldIdByKey,
    saveSpecify: (key, text) => data.writeSpecify(r.rowId, key, text),
    ...extra,
    save: (changes) => save(withSoleCleared(r.values, changes)),
  };
}

/** A tile's block: its lead, its lines or table, and the note its answers show (the board's `noteFor`). */
function TileNote({ option, note, testId }: { option: Ch2Option; note: string; testId: string }) {
  if (!note) return null;
  return <NoteCard mark={option.mark ?? ""} color={option.color ?? ""} testId={testId}>{note}</NoteCard>;
}

// ── Your home and other places (s233, D-233.81; board v440; s237 PR 4, D-236.47(2)–(3)) ─────────────────────────────

/**
 * s237 (D-236.47(2), D-236.88-6, -7): the home's answer is asked once, in Personal details ("Is this home…"), so this
 * screen draws no "Where you live" tiles — only "Your home" and ONE line carried from chapter one: the address, the
 * answer, "from Personal details." and "Change it there", which opens Personal details with a way back to here.
 * Owned / Rented PIN the home as row 1 of Places you own / Places you rent (the board's `pinTo`), in the TABLE's mark
 * (D-236.56, rule 146), and that table comes first. With family has no home row (D-236.47(3), D-237.41): its block
 * ("Family or care home") opens under the line, its answers on their own row (./useHome).
 */
/** Where "Change it there" opens and comes back from (rule 127: Personal details as a visit, with its Return). */
export const PERSONAL_DETAILS_FROM_CH2 = "/plan/screen/you?from=chapter-2";

/** The line's address: street and city, as the board draws it ("40 Raymond Ave, Toronto"). Pure. */
export function homeLineAddress(stored: string | undefined): string {
  const a = parseAddress(stored ?? "");
  return a ? [a.street, a.city].filter((x) => (x ?? "").trim()).join(", ") : (stored ?? "").trim();
}

/** s237 PR 5 (D-237.52, board v465 `:1341`): the home row's line — "Your home — owned, from Personal details." Pure. */
export function homeRowLine(tenure: "own" | "rent"): string {
  return `${CH2_HOME_TEMPLATE.head} — ${CH2_HOME_TEMPLATE.now[tenure]}, ${CH2_HOME_TEMPLATE.from}`;
}

/** The one line: "<address> — <answer>, from Personal details." Unanswered or no address drops that part. Pure. */
export function homeLine(address: string, tenure: Tenure | null): string {
  const lead = [address, tenure ? CH2_HOME_TEMPLATE.now[tenure] : ""].filter(Boolean).join(" — ");
  return lead ? `${lead}, ${CH2_HOME_TEMPLATE.from}` : CH2_HOME_TEMPLATE.from.charAt(0).toUpperCase() + CH2_HOME_TEMPLATE.from.slice(1);
}

export function HomeAndPropertyScreen({ clientId, onAnswered }: { clientId: string; onAnswered: () => void }) {
  return <OnePanelProvider><HomeAndPropertyBody clientId={clientId} onAnswered={onAnswered} /></OnePanelProvider>;
}

function HomeAndPropertyBody({ clientId, onAnswered }: { clientId: string; onAnswered: () => void }) {
  const about = useAboutYou(clientId, onAnswered);
  // s237 (rule 159): the home — its answer, its row, the create lock and the switch question — is ONE hook, shared with
  // chapter one's "Is this home…" (./useHome).
  const homeOf = useHome(clientId, onAnswered, about);
  const { props, rentals, own, home, famRow, started, pendingId, tenure } = homeOf;
  const [, setLocation] = useLocation();
  const [famNote, setFamNote] = useState("");
  const fam = (PROP.options ?? []).find((o) => o.grp === "live" && o.id === "fam");
  useReportNoneAnswer("interview-prop-fam", tenure === "family", fam?.prune ? { prune: fam.prune } : undefined);
  const onFamNote = useCallback((n: string) => setFamNote((m) => (m === n ? m : n)), []);
  if (!homeOf.ready) return <div data-testid="interview-prop-loading" />;

  /** The pinned home row (or one not saved yet — its first answer carries what makes it this home, the seed). */
  const pinOf = (t: Tenure, data: ReturnType<typeof useSectionRows>, row: SectionRow | undefined): Ch2RowIO => {
    const r = row ?? { rowId: started ?? pendingId, hidden: false, values: {} };
    // D-236.56 (rule 146): the home row takes its TABLE's mark and colour — Places you own / Places you rent.
    const table = t === "own" ? optionOf(PROP, "own") : t === "rent" ? optionOf(PROP, "rent") : undefined;
    return rowIO(clientId, data, r, {
      pinned: true,
      ...(table?.mark && table.color ? { mark: { mark: table.mark, color: table.color } } : {}),
      ...(t === "own" ? { skipGift: (v: Readonly<Record<string, string>>) => coOwnerIsSpouse(about.values, v[FIELD_KEYS.JOINT_OWNER_NAME] ?? "") } : {}),
      // s237 PR 5 (D-237.52, board v465 `:1341`): the home row's one line — the answer lives in Personal details.
      ...(t !== "family" ? { homeLine: { text: homeRowLine(t), change: CH2_HOME_TEMPLATE.change, onChange: () => setLocation(PERSONAL_DETAILS_FROM_CH2) } } : {}),
      ...(!row ? { save: (changes: Record<string, string>) => data.writeCells(r.rowId, { ...homeOf.seedFor(t), ...changes }) } : {}),
    });
  };
  const pins: Partial<Record<string, Ch2RowIO>> = tenure === "own" ? { own: pinOf("own", props, own) } : tenure === "rent" ? { rent: pinOf("rent", rentals, home) } : {};
  /** s235 PR B (D-235.65): a home row that no longer matches the answer asks one thing (./useHome, D-237.40, -41). */
  const stillHere = (tileId: string, r: SectionRow) => (tileId === "own" || tileId === "rent" ? homeOf.stillHere(tileId, r) : undefined);

  // s237 PR 5 (D-237.52): Owned / Rented draw no block here — the home is row 1 of its table, its line under it. The block
  // stays for With family (no home row) and no answer.
  const where = tenure === "own" || tenure === "rent" ? null : (
    <div data-testid="interview-prop-live">
      <div className="flex max-w-[1120px] flex-col gap-1.5">
        <h2 className="text-[17px] text-warm-charcoal" data-testid="interview-prop-live-head">{CH2_HOME_TEMPLATE.head}</h2>
        {/* D-236.88-6: one line that follows the answer, rule 145's colour; D-236.88-7: Change it there opens Personal details. */}
        <p className="text-[14px] text-warm-muted" data-testid="interview-prop-live-line">
          {homeLine(homeLineAddress(about.values[FIELD_KEYS.HOME_ADDRESS]), tenure)}{" "}
          <button type="button" onClick={() => setLocation(PERSONAL_DETAILS_FROM_CH2)}
            className="text-sage-600 underline hover:text-sage-800" data-testid="interview-prop-live-change">{CH2_HOME_TEMPLATE.change}</button>
        </p>
      </div>
      {tenure === "family" && fam && (
        // The board's `oneLine` block: its own title, the four questions on one line, on the family answers' own row.
        <div className="mt-7 flex flex-col gap-4 border-t border-warm-stone pt-6" data-testid="interview-prop-fam">
          {fam.lead && <span className="text-[15px] text-warm-charcoal" data-testid="interview-prop-fam-lead">{fam.lead}</span>}
          <Ch2Lines where={{ screen: "prop", tile: "fam", testId: "interview-prop-fam" }} lines={fam.lines ?? []} io={pinOf("family", rentals, famRow)} noteFor={fam.noteFor} onNote={onFamNote} oneLine={fam.oneLine} />
          <TileNote option={fam} note={famNote} testId="interview-prop-note-fam" />
        </div>
      )}
    </div>
  );

  // s234 walk: only the PINNED home leaves Places you own — a home flagged where the planner does not say Owned is still
  // an owned place, shown there with its "still own?" line, never lost. A flagged rented row shows in Places you rent only
  // when it asks (D-235.65); the pinned rented home and the family answers' row are drawn above.
  const ownTile: TableTile = { id: "own", data: props, owns: (r) => !(tenure === "own" && own && r.rowId === own.rowId) };
  const rentTile: TableTile = { id: "rent", data: rentals, owns: (r) => r.values[FIELD_KEYS.IS_RESIDENCE] !== "true" || !!homeOf.stillHere("rent", r) };
  return (
    // D-236.47(2): the table holding the home comes first.
    <TablesScreen step={PROP} screen="prop" clientId={clientId} mode="multi" testId="interview-prop" before={where} pins={pins} stillHere={stillHere}
      tileIds={["own", "rent"]} tiles={tenure === "rent" ? [rentTile, ownTile] : [ownTile, rentTile]} />
  );
}

// ── the three table screens ───────────────────────────────────────────────────────────────────────

type TableTile = {
  id: string;
  data: ReturnType<typeof useSectionRows>;
  /** Which of the section's rows belong to this tile. */
  owns: (r: SectionRow) => boolean;
  /** What a new row of this tile starts with. */
  start?: Record<string, string>;
  /** s232 PR C: after a row's answers are saved (Firearms: a firearm kept locked up carries its gun safe to Safes). */
  afterSave?: (changes: Record<string, string>) => Promise<unknown>;
};

/** A tiles-then-tables screen (Other property, Vehicles, Valuables): tiles from the board, one table per picked tile. */
function TablesScreen({ step, screen, clientId, tiles, mode, testId, onAnswer, before, pins, tileIds, stillHere }: {
  step: Ch2Step; screen: string; clientId: string; tiles: TableTile[]; mode: "multi" | "single"; testId: string;
  /** s232 PR C: the tile answer itself (Firearms writes the has-firearms flag, as Pets writes has-pets). */
  onAnswer?: (next: TileSelection, changed: string) => Promise<unknown>;
  /** s233 (D-233.81): what the combined screen shows above these tiles (Where you live). */
  before?: ReactNode;
  /** s233 (D-233.81): a pinned home row per table (row 1, no ×); its table shows even when its tile is not picked. */
  pins?: Partial<Record<string, Ch2RowIO>>;
  /** s233 (D-233.81): which of the step's options are these tiles (the rest belong to `before`); and their heading. */
  tileIds?: readonly string[];
  /** s235 PR B (D-235.65): a row's "Do you still own / rent …?" line — the question, and what Keep writes. */
  stillHere?: (tileId: string, r: SectionRow) => { question: string; keep: Record<string, string> } | undefined;
}) {
  const createFlight = useSingleFlight();
  // Rows started this visit, by the tile they were started under (a started row holds nothing yet to tell its tile).
  const [createdHere, setCreatedHere] = useState<ReadonlyMap<string, string>>(() => new Map());
  const [notes, setNotes] = useState<Record<string, string>>({});
  const ready = tiles.every((t) => t.data.ready);
  const rowsOf = (t: TableTile) => t.data.rows.filter((r) => isRecordRow(r.rowId) && t.owns(r));
  /** The fields a tile's table asks (its columns and its row lines) — a row holding any of them is an answer. */
  const answerKeys = (t: TableTile) =>
    Array.from(new Set((optionOf(step, t.id).rows?.cells ?? []).flatMap((c) => c.kind === "lines" ? (c.lines ?? []) : [c]).map((c) => fieldOf(screen, t.id, c)).filter(Boolean)));
  const shown = (t: TableTile) => rowsOf(t).filter((r) => !r.hidden && (createdHere.has(r.rowId) || hasAny(r, [...answerKeys(t), FIELD_KEYS.NOTES])));

  const [selection, setSelection] = useState<TileSelection | null>(null);
  useEffect(() => {
    if (selection || !ready) return;
    setSelection({ selected: tiles.filter((t) => shown(t).length > 0).map((t) => t.id), none: false });
  });
  const sel = selection ?? { selected: [], none: false };

  const add = async (t: TableTile) => {
    const rowId = crypto.randomUUID();
    // Class A: a second fire is a second row. The row is drawn at once and exists on its first answer (rule 15).
    await createFlight.run(async () => { if (t.start && Object.keys(t.start).length) await t.data.createRow(t.start, rowId); });
    setCreatedHere((m) => new Map(m).set(rowId, t.id));
  };
  const hide = async (t: TableTile, rows: readonly SectionRow[]) => {
    for (const r of rows) if (!r.hidden && Object.keys(r.values).length) await t.data.setRowHidden(r.rowId, true);
    t.data.refresh();
  };
  const onTiles = async (next: TileSelection, changed: string) => {
    setSelection(next);
    if (onAnswer) await onAnswer(next, changed);
    if (changed === "none") { if (next.none) for (const t of tiles) await hide(t, shown(t)); return; }
    const t = tiles.find((x) => x.id === changed)!;
    if (next.selected.includes(changed)) {
      const hidden = rowsOf(t).filter((r) => r.hidden && hasAny(r, answerKeys(t)));
      if (hidden.length) { for (const r of hidden) await t.data.setRowHidden(r.rowId, false); t.data.refresh(); }
      else if (shown(t).length === 0) await add(t);
    } else await hide(t, shown(t));
  };
  const onNote = useCallback((id: string) => (n: string) => setNotes((m) => (m[id] === n ? m : { ...m, [id]: n })), []);
  const noteSetters = useMemo(() => Object.fromEntries(tiles.map((t) => [t.id, onNote(t.id)])), [tiles.map((t) => t.id).join(","), onNote]);

  if (!ready) return <div data-testid={`${testId}-loading`} />;
  const own = (o: Ch2Option) => !tileIds || tileIds.includes(o.id) || (o.none && !!tileIds);
  const tilesHere = (step.options ?? []).filter((o) => !o.none && own(o)).map(({ id, label, sub, mark, color }) => ({ id, label, sub, mark, color }));
  const tilesHead = tileIds ? (step.options ?? []).find((o) => tileIds.includes(o.id) && o.head)?.head : undefined;
  return (
    <div data-testid={testId}>
      {before}
      {tilesHead && <h2 className="mb-3 mt-8 text-[17px] text-warm-charcoal" data-testid={`${testId}-others-head`}>{tilesHead}</h2>}
      <TilePicker tiles={tilesHere} selection={sel} mode={mode} none={noneOf(step)} columns={3} compact
        onChange={(n, c) => saveInBackground(onTiles(n, c))} testIdPrefix={testId} />
      {tiles.filter((t) => sel.selected.includes(t.id) || pins?.[t.id]).map((t) => {
        const o = optionOf(step, t.id);
        const picked = sel.selected.includes(t.id);
        const drawn = picked ? [...shown(t), ...Array.from(createdHere).filter(([id, tid]) => tid === t.id && !rowsOf(t).some((r) => r.rowId === id))
          .map(([id]) => ({ rowId: id, hidden: false, values: {} as Record<string, string> }))] : [];
        if (!o.rows) return null;
        const pin = pins?.[t.id];
        return (
          <div key={t.id} data-testid={`${testId}-${t.id}`}>
            <Ch2Table screen={screen} tile={t.id} rows={o.rows} lead={o.lead} mark={o.mark && o.color ? { mark: o.mark, color: o.color } : undefined} testId={`${testId}-${t.id}-table`} noteFor={o.noteFor} onNote={noteSetters[t.id]}
              data={[...(pin ? [pin] : []), ...drawn.map((r) => {
                // s235 PR B (D-235.65): Keep writes the row's answer; Remove is the row's own delete (the × path below).
                const still = stillHere?.(t.id, r);
                const io = rowIO(clientId, t.data, r, still ? { stillHere: { question: still.question,
                  onKeep: () => saveInBackground(t.data.writeCells(r.rowId, still.keep)), onRemove: () => saveInBackground(hide(t, [r])) } } : undefined);
                return t.afterSave ? { ...io, save: async (c: Record<string, string>) => { await io.save(c); await t.afterSave!(c); } } : io;
              })]}
              onAdd={() => { if (!picked) setSelection((s) => ({ selected: [...(s?.selected ?? []), t.id], none: false })); saveInBackground(add(t)); }}
              onRemove={(rowId) => { const r = rowsOf(t).find((x) => x.rowId === rowId); saveInBackground(r ? hide(t, [r]) : Promise.resolve(setCreatedHere((m) => { const n = new Map(m); n.delete(rowId); return n; }))); }} />
            {pin && o.rows.pinNote && <div className="mt-3"><TileNote option={o} note={o.rows.pinNote} testId={`${testId}-pinnote-${t.id}`} /></div>}
            <div className="mt-3"><TileNote option={o} note={notes[t.id] ?? ""} testId={`${testId}-note-${t.id}`} /></div>
          </div>
        );
      })}
      {step.foot && <p className="mt-6 text-[14px] text-warm-muted" data-testid={`${testId}-foot`}>{step.foot}</p>}
    </div>
  );
}

export function VehiclesScreen({ clientId, onAnswered }: { clientId: string; onAnswered: () => void }) {
  const vehicles = useSectionRows(clientId, CATEGORY_SLUGS.ASSETS, SECTION_SLUGS.ASSETS_VEHICLE_LIST, onAnswered, { typed: true });
  return <OnePanelProvider><TablesScreen step={VEH} screen="veh" clientId={clientId} tiles={[{ id: "yes", data: vehicles, owns: () => true }]} mode="single" testId="interview-veh" /></OnePanelProvider>;
}

/** Firearms are their own screen (ruling 3: `valuables` rows with category firearm), so Valuables leaves them out. */
const FIREARM = "Firearms";
export function ValuablesScreen({ clientId, onAnswered }: { clientId: string; onAnswered: () => void }) {
  const valuables = useSectionRows(clientId, CATEGORY_SLUGS.ASSETS, SECTION_SLUGS.ASSETS_VALUABLES, onAnswered, { typed: true });
  return <OnePanelProvider><TablesScreen step={VAL} screen="val" clientId={clientId} tiles={[{ id: "yes", data: valuables, owns: (r) => r.values[FIELD_KEYS.CATEGORY] !== FIREARM }]} mode="single" testId="interview-val" /></OnePanelProvider>;
}


// ── Firearms and Safes (s232 PR C; rulings 3 and 4) ─────────────────────────────────────────────────

const FA = boardStepCh2("fa");
const SAFES = boardStepCh2("safes");
/** The Safes row a locked-up firearm carries across (rule 14): its place, stored in Safes' own words. */
export const GUN_SAFE = "Gun safe";

/**
 * Firearms — Valuables rows with category Firearms (ruling 3). The tile answer is the plan's has-firearms flag (it gates
 * the executor's firearms task), written as Pets writes has-pets. A firearm kept "In a gun safe or a locked cabinet?"
 * Yes carries ONE gun safe row onto Safes (rule 14: a row one screen writes on another; it is asked where it lands).
 */
export function FirearmsScreen({ clientId, onAnswered }: { clientId: string; onAnswered: () => void }) {
  const valuables = useSectionRows(clientId, CATEGORY_SLUGS.ASSETS, SECTION_SLUGS.ASSETS_VALUABLES, onAnswered, { typed: true });
  const safes = useSectionRows(clientId, CATEGORY_SLUGS.ASSETS, SECTION_SLUGS.ASSETS_SAFES_LOCKED_PLACES, onAnswered, { typed: true });
  const onSaveFailed = useSaveFailure();
  const flag = useMutation({
    mutationFn: async (hasFirearms: boolean) => await apiRequest("PATCH", `/api/clients/${clientId}/situation-flags`, { hasFirearms }),
    onError: onSaveFailed,
  });
  const carryFlight = useSingleFlight();
  const carryGunSafe = async (changes: Record<string, string>) => {
    if (changes[FIELD_KEYS.IN_LOCKED_STORAGE] !== "true") return;
    if (safes.rows.some((r) => isRecordRow(r.rowId) && r.values[FIELD_KEYS.PLACE_KIND] === GUN_SAFE)) return;
    // Class A: a second fire would be a second gun safe — one lock.
    await carryFlight.run(() => safes.createRow({ [FIELD_KEYS.PLACE_KIND]: GUN_SAFE }));
  };
  const tiles: TableTile[] = [{ id: "yes", data: valuables, owns: (r) => r.values[FIELD_KEYS.CATEGORY] === FIREARM, start: { [FIELD_KEYS.CATEGORY]: FIREARM }, afterSave: carryGunSafe }];
  return (
    <OnePanelProvider>
      <TablesScreen step={FA} screen="fa" clientId={clientId} tiles={tiles} mode="single" testId="interview-fa"
        onAnswer={async (next, changed) => {
          if (changed === "none" && next.none) await flag.mutateAsync(false);
          else if (changed === "yes" && next.selected.includes("yes")) await flag.mutateAsync(true);
        }} />
    </OnePanelProvider>
  );
}

/** Safes and Locked Places — located_items rows (ruling 4). The gun safe carried from Firearms is one of them. */
export function SafesScreen({ clientId, onAnswered }: { clientId: string; onAnswered: () => void }) {
  const safes = useSectionRows(clientId, CATEGORY_SLUGS.ASSETS, SECTION_SLUGS.ASSETS_SAFES_LOCKED_PLACES, onAnswered, { typed: true });
  return <OnePanelProvider><TablesScreen step={SAFES} screen="safes" clientId={clientId} tiles={[{ id: "yes", data: safes, owns: () => true }]} mode="single" testId="interview-safes" /></OnePanelProvider>;
}
